import * as djangioActionCreators from "shared/djangio/action-creators"
import * as actionGroups from "shared/stakeholder/action-types"

/**
 * This function does an initial fetch of the AlignmentTypes for the current User, and then saves it in the Redux store.
 * The user will need to navigate to a Profile, Sheet or Official/Staffer/Supporter search before it's needed,
 * so we can reduce the time to interactive by making it an asynchronous fetch on the initial page load.
 *
 * When loading a widget that actually requires this data, we can fire another request to ensure the user sees the most up to date
 * information - in the rare occasions when this collection has changed since first load.
 * @name getAlignmentTypes
 * @function
 */
export const getAlignmentTypes = () =>
    djangioActionCreators.resourceGetList(
        DjangIO.app.userdata.stakeholder.models.AlignmentType,
        actionGroups.loadAlignmentTypesActionDict,
        {
            archived: false,
            dehydrate_extra: ["username"],
        },
        {},
        true,
    )

/**
* This is the same as getAlignmentTypes above, but it goes through the /api/sheet/ endpoint.
*
* We need to go through the Sheets endpoint, because External Sheet users are not authenticated,
* but the Sheets endpoint is whitelisted in app.api.classes.QuorumSessionAuthentication.is_authenticated()
*
* @name getAlignmentTypesForExternalSheets
* @function
* @param {string} sheetKey - An external sheet slug. We include this so that is_authenticated() can attach a user to the Request object,
    and we can get the appropriate AlignmentTypes through that User's Organization.
*/
export const getAlignmentTypesForExternalSheets = (sheetKey) =>
    djangioActionCreators.sendAction(
        DjangIO.app.userdata.spreadsheet.models.Sheet.objects.get(sheetKey),
        actionGroups.loadAlignmentTypesActionDict,
        {
            action: "get_alignment_types",
            kwargs: {
                archived: false,
            },
            method: "get",
            payload: {},
        },
    )

/**
 * This function loads a single AlignmentType for modification
 * This function is unused at the moment, but is expected to be needed in the future,
 * so we include it here for completeness.
 * @param {number} alignmentTypeId - ID of the AlignmentType to be loaded
 * @name loadAlignmentType
 * @function
 */
export const loadAlignmentType = (alignmentTypeId) =>
    djangioActionCreators.resourceGetDetail(
        DjangIO.app.userdata.stakeholder.models.AlignmentType,
        actionGroups.loadAlignmentTypeActionDict,
        alignmentTypeId,
    )

/**
 * This function updates a single AlignmentType
 * @param {number} alignmentTypeId - ID of the AlignmentType to be updated
 * @param {object} kwargs - A map of the fields to be updated
 * @name updateAlignmentType
 * @function
 */
export const updateAlignmentType = (alignmentTypeId, kwargs) =>
    djangioActionCreators.resourcePatchDetail(
        DjangIO.app.userdata.stakeholder.models.AlignmentType,
        actionGroups.updateAlignmentTypeActionDict,
        alignmentTypeId,
        kwargs,
    )

/**
 * This function fetches the most recent AlignmentAction by filtering for a null effective_end_date, of which there should only
 * ever be one per Organization and ProfileID.
 * @name loadProfileAlignment
 * @function
 * @param {object} qdt - QDT of the current Profile
 * @param {number} profileId - ID of the current Profile
 * @param {object} actionDict - Map of ActionTypes for this request (Allows using the same ActionCreator with the DLC reducer)
 * @param {object} payload - Optional payload (used in the DLC reducer)
 */
export const loadProfileAlignment = ({
    qdt,
    profileId,
    actionDict = actionGroups.loadProfileAlignmentActionDict,
    payload = {},
}) =>
    djangioActionCreators.resourceGetList(
        DjangIO.app.userdata.stakeholder.models.AlignmentAction,
        actionDict,
        {
            [qdt.related_field]: profileId,
            issue: null,
            effective_end_date__isnull: true,
            dehydrate_extra: ["fullname", "username"],
        },
        payload,
    )

/**
 * This function POSTs a new AlignmentAction for the current Profile
 * @name postProfileAlignment
 * @function
 * @param {object} qdt - QDT of the current Profile
 * @param {number} profileId - ID of the current Profile
 * @param {number} alignmentTypeId - ID of the AlignmentType being assigned
 */
export const postProfileAlignment = ({ qdt, profileId, alignmentTypeId }) =>
    djangioActionCreators.resourcePostList(
        DjangIO.app.userdata.stakeholder.models.AlignmentAction,
        actionGroups.postProfileAlignmentActionDict,
        {
            [qdt.related_field]: profileId && DjangIO.newResourceFromDataType(qdt.value).resourceUriFromId(profileId),
            alignment_type: DjangIO.app.userdata.stakeholder.models.AlignmentType.resourceUriFromId(alignmentTypeId),
        },
        {
            profileId,
            alignmentTypeId,
        },
    )

/**
 * This function calls an API action to update the effective_end_date of the currently active AlignmentAction,
 * thereby marking it as inactive and removing the Alignment from the given Profile
 * @name removeProfileAlignment
 * @function
 * @param {object} qdt - QDT of the current Profile
 * @param {number} profileId - ID of the current Profile
 */
export const removeProfileAlignment = ({ qdt, profileId }) =>
    djangioActionCreators.sendAction(
        DjangIO.app.userdata.stakeholder.models.AlignmentAction,
        actionGroups.removeProfileAlignmentActionDict,
        {
            action: "remove_alignment",
            kwargs: {
                [qdt.related_pk_field]: profileId,
            },
            method: "get",
            payload: {
                dataType: qdt.value,
                profileId,
            },
        },
    )

/**
 * This function returns a map of Issue IDs and their current AlignmentType ID
 * @name loadIssueAlignments
 * @function
 * @param {object} qdt - QDT of the current Profile
 * @param {number} profileId - ID of the current Profile
 * @param {array} issueIds - Array of Issue IDs that will be queried for Alignments
 * @returns {object} - A map of Issue IDs to their current AlignmentType ID
 */
export const loadIssueAlignments = ({ qdt, profileId, issueIds }) =>
    djangioActionCreators.sendAction(
        DjangIO.app.userdata.stakeholder.models.AlignmentAction,
        actionGroups.loadIssueAlignmentsActionDict,
        {
            action: "load_issue_alignments",
            kwargs: {
                [qdt.related_pk_field]: profileId,
                issue_ids: issueIds,
            },
            method: "get",
            payload: {
                dataType: qdt.value,
                profileId,
            },
        },
    )

/**
 * This function creates a new AlignmentAction for the given Profile/Issue pair
 * @name postIssueAlignment
 * @function
 * @param {object} qdt - QDT of the current Profile
 * @param {number} profileId - ID of the current Profile
 * @param {number} issueId - ID of the given Issue
 * @param {number} alignmentTypeId - The AlignmentType to be assigned
 */
export const postIssueAlignment = ({ qdt, profileId, issueId, alignmentTypeId }) =>
    djangioActionCreators.resourcePostList(
        DjangIO.app.userdata.stakeholder.models.AlignmentAction,
        actionGroups.postIssueAlignmentActionDict,
        {
            [qdt.related_field]: DjangIO.newResourceFromDataType(qdt.value).resourceUriFromId(profileId),
            issue: DjangIO.app.userdata.models.IssueManagement.resourceUriFromId(issueId),
            alignment_type: DjangIO.app.userdata.stakeholder.models.AlignmentType.resourceUriFromId(alignmentTypeId),
        },
        {
            dataType: qdt.value,
            profileId,
            issueId,
            alignmentTypeId,
        },
    )

/**
 * This function calls an API action to update the effective_end_date of the currently active AlignmentAction,
 * thereby marking it as inactive and removing the Alignment from the given Profile/Issue pair
 * @name removeIssueAlignment
 * @function
 * @param {object} qdt - QDT of the current Profile
 * @param {number} profileId - ID of the current Profile
 * @param {number} issueId - ID of the given Issue
 */
export const removeIssueAlignment = ({ qdt, profileId, issueId }) =>
    djangioActionCreators.sendAction(
        DjangIO.app.userdata.stakeholder.models.AlignmentAction,
        actionGroups.removeIssueAlignmentActionDict,
        {
            action: "remove_alignment",
            kwargs: {
                [qdt.related_pk_field]: profileId,
                issue_id: issueId,
            },
            method: "get",
            payload: {
                dataType: qdt.value,
                profileId,
                issueId,
            },
        },
    )
